/**
 * Left rail: one flyout per semantic group, each with a muted tone (ui-portal group tones,
 * @evo-flywheel/ui). Groups come from the descriptors' `ui.navGroup`; the "Panoramica" group
 * carries the kit's own surfaces (dashboard, guided journeys, rule tables).
 */
import React from "react";
import { DashboardOutlined, CompassOutlined, ExperimentOutlined, SafetyOutlined, AlertOutlined, FileProtectOutlined, AuditOutlined, TeamOutlined, ReconciliationOutlined, ApartmentOutlined } from "@ant-design/icons";
import { getIconComponent, type HubNavGroup } from "@evo-flywheel/ui";
import type { RefineResource } from "@kit/refine-resources";
import { descriptors } from "@kit/descriptors/index";
import { instance } from "./instance";

/** Muted hues (AA on white). A profile may retune them via `tones`. */
export const GROUPS: Array<{ key: string; label: string; icon: React.ReactNode; tone: string }> = [
  { key: "home", label: "Panoramica", icon: <DashboardOutlined />, tone: "#282A64" },
  { key: "GDPR", label: "GDPR", icon: <SafetyOutlined />, tone: "#016BA5" },
  { key: "Rischi", label: "Rischi e misure", icon: <AlertOutlined />, tone: "#B4532A" },
  { key: "Policy", label: "Policy e accettazioni", icon: <FileProtectOutlined />, tone: "#0E7C72" },
  { key: "Audit", label: "Audit", icon: <AuditOutlined />, tone: "#7A4E9C" },
  { key: "Sicurezza Lavoro", label: "Sicurezza lavoro (81/08)", icon: <TeamOutlined />, tone: "#9A6B1F" },
  { key: "Antiriciclaggio", label: "Antiriciclaggio", icon: <ReconciliationOutlined />, tone: "#5B6B2E" },
  { key: "Organizzazione", label: "Organizzazione", icon: <ApartmentOutlined />, tone: "#4A5568" },
];

const groupOf = new Map(descriptors.map((d) => [d.slug, typeof d.ui === "object" && d.ui && "navGroup" in d.ui ? (d.ui as { navGroup?: string }).navGroup : undefined]));
const iconOf = new Map(descriptors.map((d) => [d.slug, typeof d.ui === "object" && d.ui && "icon" in d.ui ? (d.ui as { icon?: string }).icon : undefined]));

export const toneOfGroup = (key?: string) => {
  const g = GROUPS.find((x) => x.key === key);
  return g ? instance.tones?.[g.key] ?? g.tone : undefined;
};

export function buildNav(resources: RefineResource[]): HubNavGroup[] {
  const home: HubNavGroup["items"] = [
    { key: "dashboard", label: "Cruscotto", path: "/dashboard", icon: <DashboardOutlined /> },
    { key: "wizard", label: "Percorsi guidati", path: "/wizard", icon: <CompassOutlined /> },
    { key: "regole", label: "Regole e verifiche", path: "/regole", icon: <ExperimentOutlined /> },
  ];
  return GROUPS.map((g) => {
    const items = g.key === "home" ? home : resources
      .filter((r) => groupOf.get(r.name) === g.key)
      .map((r) => {
        return { key: r.name, label: r.meta.label, path: `/${r.name}`, icon: getIconComponent(iconOf.get(r.name) ?? "AppstoreOutlined") };
      });
    return { key: g.key, label: g.label, icon: g.icon, tone: toneOfGroup(g.key), items };
  }).filter((g) => g.items.length > 0);
}
